import { useState, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  format,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { MonthView } from "@/components/calendar/MonthView";
import { WeekTimeline } from "@/components/calendar/WeekTimeline";
import { DayDetail } from "@/components/calendar/DayDetail";
import { CreateEventDialog } from "@/components/calendar/CreateEventDialog";
import { EventEditDialog } from "@/components/calendar/EventEditDialog";
import { ItemEditSheet } from "@/components/items/ItemEditSheet";
import { useCalendarEventsRange } from "@/hooks/useCalendar";
import type { CalendarEvent } from "@/hooks/useCalendar";
import { useWeekItems } from "@/hooks/useItems";
import type { Item } from "@/lib/types";
import { cn } from "@/lib/utils";

type ViewMode = "month" | "week";

export default function Calendar() {
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createEventDate, setCreateEventDate] = useState<Date | undefined>();
  const [createEventTime, setCreateEventTime] = useState<string | undefined>();
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);

  // Compute visible range based on view mode
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (viewMode === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      // Extend to cover full grid weeks
      return {
        rangeStart: startOfWeek(monthStart, { weekStartsOn: 1 }),
        rangeEnd: endOfWeek(monthEnd, { weekStartsOn: 1 }),
      };
    } else {
      return {
        rangeStart: startOfWeek(currentDate, { weekStartsOn: 1 }),
        rangeEnd: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    }
  }, [currentDate, viewMode]);

  const rangeStartStr = format(rangeStart, "yyyy-MM-dd");
  const rangeEndStr = format(rangeEnd, "yyyy-MM-dd");
  const timeMin = rangeStart.toISOString();
  const timeMax = rangeEnd.toISOString();

  // Fetch calendar events for visible range
  const { data: events = [], isLoading: eventsLoading } =
    useCalendarEventsRange(timeMin, timeMax);

  // Fetch items (tasks) for visible range
  const { data: items = [], isLoading: itemsLoading } = useWeekItems(
    rangeStartStr,
    rangeEndStr
  );

  const isLoading = eventsLoading || itemsLoading;

  // Filter events/items for selected day
  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return events.filter((e) => e.start.slice(0, 10) === dateStr);
  }, [events, selectedDate]);

  const selectedDayItems = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return (items || []).filter((i) => i.date_target === dateStr);
  }, [items, selectedDate]);

  // Navigation
  const goNext = () => {
    if (viewMode === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goPrev = () => {
    if (viewMode === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleSelectDate = (date: Date) => {
    setSelectedDate(date);
  };

  const handleAddEvent = (date?: Date, startTime?: string) => {
    setCreateEventDate(date || selectedDate || new Date());
    setCreateEventTime(startTime);
    setCreateEventOpen(true);
  };

  const handleTimeSlotClick = (date: Date, hour: number) => {
    handleAddEvent(date, `${String(hour).padStart(2, "0")}:00`);
  };

  // Header label
  const headerLabel = useMemo(() => {
    if (viewMode === "month") {
      return format(currentDate, "MMMM yyyy");
    }
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    const we = endOfWeek(currentDate, { weekStartsOn: 1 });
    return `${format(ws, "MMM d")} - ${format(we, "MMM d, yyyy")}`;
  }, [currentDate, viewMode]);

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goPrev}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold min-w-[10rem] text-center">
            {headerLabel}
          </h1>
          <Button variant="ghost" size="icon" onClick={goNext}>
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
        </div>

        {/* View toggle */}
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => setViewMode("month")}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-colors",
              viewMode === "month"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            Month
          </button>
          <button
            onClick={() => setViewMode("week")}
            className={cn(
              "px-3 py-1.5 text-xs font-medium transition-colors",
              viewMode === "week"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            Week
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading...</p>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Main calendar view */}
          <div className="flex-1 min-w-0">
            {viewMode === "month" ? (
              <MonthView
                currentMonth={currentDate}
                events={events}
                items={items || []}
                selectedDate={selectedDate}
                onSelectDate={handleSelectDate}
              />
            ) : (
              <WeekTimeline
                currentWeekStart={startOfWeek(currentDate, {
                  weekStartsOn: 1,
                })}
                events={events}
                items={items || []}
                onSelectDate={handleSelectDate}
                onTimeSlotClick={handleTimeSlotClick}
                onTaskClick={setSelectedItem}
                onEventClick={setEditingEvent}
              />
            )}
          </div>

          {/* Day detail panel (right side on desktop, below on mobile) */}
          {selectedDate && (
            <div className="lg:w-80 lg:shrink-0">
              <div className="rounded-lg border border-border bg-card p-4 sticky top-4">
                <DayDetail
                  date={selectedDate}
                  events={selectedDayEvents}
                  tasks={selectedDayItems}
                  onAddEvent={() => handleAddEvent(selectedDate)}
                  onTaskClick={setSelectedItem}
                  onEventClick={setEditingEvent}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create event dialog */}
      <CreateEventDialog
        open={createEventOpen}
        onOpenChange={setCreateEventOpen}
        defaultDate={createEventDate}
        defaultStartTime={createEventTime}
      />

      {/* Edit event dialog */}
      <EventEditDialog
        event={editingEvent}
        open={!!editingEvent}
        onOpenChange={(open) => !open && setEditingEvent(null)}
      />

      {/* Task edit sheet */}
      <ItemEditSheet
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => !open && setSelectedItem(null)}
      />
    </AppLayout>
  );
}
